import { motion } from "framer-motion";
import { Star, QrCode } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-24 pb-40 md:pt-28 md:pb-[195px] overflow-hidden"
      data-testid="hero-section"
    >
      {/* Forest trail photo backdrop */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_zoku-wellness-hub/artifacts/1icyclfn_Untitled%20design%20%2815%29.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center"
          draggable={false}
        />
        {/* Dark readability overlay so white text pops */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(28,25,23,0.55) 0%, rgba(28,25,23,0.35) 30%, rgba(28,25,23,0.15) 55%, rgba(253,251,247,0) 80%, rgba(253,251,247,0) 100%)",
          }}
        />
        {/* Soft sun glow warm-up */}
        <div
          className="absolute left-[60%] top-[18%] w-[460px] h-[460px] rounded-full opacity-55 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(255,245,210,0.9) 0%, rgba(255,230,170,0.3) 50%, rgba(255,230,170,0) 80%)",
          }}
        />
        {/* Bottom fade into section edge */}
        <div
          className="absolute inset-x-0 bottom-0 h-[18%]"
          style={{
            background:
              "linear-gradient(0deg, #FDFBF7 0%, rgba(253,251,247,0) 100%)",
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 relative z-10">
        {/* Centered text block */}
        <div className="text-center max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.25)]"
            data-testid="hero-headline"
          >
            Health coach
            <br />
            for the <span className="font-editorial text-zoku-orange">modern athlete.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-[0_1px_6px_rgba(0,0,0,0.25)]"
          >
            Turn your health data into clear, actionable insight — from wearables and bloodwork
            to sleep, strain, and everything in between.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.28 }}
            className="mt-9 flex flex-col items-center gap-5"
            id="download"
          >
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href="https://apps.apple.com/in/app/zoku-track-calories/id6762446883"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-1.5 bg-zoku-ink text-white rounded-full pl-4 pr-6 py-3.5 hover:bg-stone-800 hover:scale-[1.02] transition-all active:scale-95 shadow-lg shadow-zoku-ink/10"
                data-testid="hero-appstore-btn"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_zoku-wellness-hub/artifacts/8gdpr0ux_apple-icon-4.png"
                  alt=""
                  className="w-6 h-6 object-contain"
                  style={{ filter: "invert(1) brightness(2)" }}
                />
                <span className="text-left leading-tight">
                  <span className="block text-base font-semibold">Download app</span>
                </span>
              </a>
            </div>

            <div className="flex items-center gap-2 text-sm text-white/85 drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-zoku-orange text-zoku-orange" />
                ))}
              </div>
              <span className="font-semibold text-white">4.8</span>
              <span>/ 28.6k ratings globally</span>
            </div>
          </motion.div>
        </div>

        {/* Device area intentionally empty */}
      </div>

      {/* Floating QR card bottom-right */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.9 }}
        className="hidden md:flex absolute bottom-8 right-8 z-30 items-center gap-4 bg-zoku-ink text-white rounded-2xl p-4 shadow-2xl max-w-[280px]"
        data-testid="hero-qr-card"
      >
        <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center shrink-0 overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_zoku-wellness-hub/artifacts/g5p3yznu_Zoku%20iOS%20App.png"
            alt="Scan to install Zoku"
            className="w-full h-full object-contain p-1"
          />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/60">
            <QrCode className="w-3 h-3" /> Scan to install
          </div>
          <div className="font-semibold text-sm mt-0.5">Download for iOS</div>
          <p className="text-xs text-white/70 mt-0.5 leading-snug">
            Get started with Zoku for free today.
          </p>
        </div>
      </motion.div>
    </section>
  );
}
